import { forwardRef, type ElementType, type ReactNode } from 'react';
import { cx } from '@linaria/atomic';
import type { CommonHTMLAnchorAttributes } from '@frontend/types';
import { getFontColorClassName } from '../../helpers/font-color.linaria';
import { getTextAlignClassName } from '../../helpers/text-align.linaria';
import type { TextAlignment } from '../../helpers/text-align.linaria';
import {
  textBaseStyle,
  getTextSizeClassName,
  getTextWeightClassName,
  type FontWeight,
  type TextSize,
} from '../text/text-style-helpers';
import { buttonStyles, aTagStyles, disabledStyles, hoverStyles } from './text-link.styles';

type TextLinkProps = CommonHTMLAnchorAttributes & {
  children: ReactNode;
  className?: string;
  color?: 'primary' | 'error';
  disabled?: boolean;
  size?: TextSize;
  textAlign?: TextAlignment;
  to?: string;
  weight?: FontWeight;
  trackingId?: string;
  as?: ElementType;
};

export const TextLink = forwardRef<HTMLButtonElement | HTMLAnchorElement, TextLinkProps>(
  (
    {
      className,
      color = 'primary',
      disabled,
      onClick,
      size = 'inherit',
      textAlign,
      weight = 'inherit',
      trackingId,
      as,
      ...props
    },
    ref
  ) => {
    const isButton = !props.to && !!onClick;
    const Component: ElementType = as ?? (isButton ? 'button' : 'a');

    return (
      <Component
        type={isButton ? 'button' : undefined}
        className={cx(
          textBaseStyle,
          getTextSizeClassName(size),
          getTextWeightClassName(weight),
          textAlign && getTextAlignClassName(textAlign),
          getFontColorClassName(disabled ? 'disabled' : color),
          isButton ? buttonStyles : aTagStyles,
          disabled ? disabledStyles : hoverStyles,
          className
        )}
        href={props.to}
        onClick={onClick}
        disabled={disabled && isButton}
        ref={ref as any}
        data-trackingid={trackingId}
        {...props}
      />
    );
  }
);

TextLink.displayName = 'TextLink';
